import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import type * as mapboxgl from 'mapbox-gl';
import type { FeatureCollection } from 'geojson';
import { MapEditHistory } from './map-edit-history';

export interface MapHistoryState {
  geojson: FeatureCollection;
  center: [number, number];
}

export interface MapHistoryContext {
  draw: () => MapboxDraw;
  marker: () => mapboxgl.Marker;
  isDrawing: () => boolean;
  isBlocked: () => boolean;
  beforeRestore: () => void;
  recompute: () => void;
  unsavedChanged: (dirty: boolean) => void;
}

export class MapHistoryController {
  private readonly history = new MapEditHistory<MapHistoryState>();
  private ready = false;
  private restoring = false;
  private revision = 0;
  private recordedRevision = -1;

  constructor(private readonly context: MapHistoryContext) {}

  get canUndo(): boolean {
    return (this.context.isDrawing() || this.history.canUndo) && !this.context.isBlocked();
  }
  get canRedo(): boolean {
    return !this.context.isDrawing() && this.history.canRedo && !this.context.isBlocked();
  }
  get unsavedChanges(): boolean {
    return this.ready ? this.history.dirty : true;
  }

  start(): void {
    this.history.reset(this.snapshot());
    this.recordedRevision = this.revision;
    this.ready = true;
    this.context.unsavedChanged(false);
  }

  markDrawChanged(): void {
    this.revision++;
  }

  recordEdit(): void {
    this.markDrawChanged();
    this.record();
  }

  record(): void {
    if (!this.ready || this.restoring || !this.context.draw() || this.isDrawingPolygon()) return;
    if (this.revision === this.recordedRevision) return;
    this.history.record(this.snapshot());
    this.recordedRevision = this.revision;
    this.context.unsavedChanged(this.history.dirty);
  }

  undo(): void {
    if (this.cancelPolygonDrawing()) return;
    if (this.canUndo) this.restore(this.history.undo());
  }

  redo(): void {
    if (this.isDrawingPolygon()) return;
    if (this.canRedo) this.restore(this.history.redo());
  }

  private snapshot(): MapHistoryState {
    const center = this.context.marker().getLngLat();
    return {
      geojson: this.context.draw().getAll() as FeatureCollection,
      center: [center.lng, center.lat],
    };
  }

  private isDrawingPolygon(): boolean {
    return (this.context.draw() as any)?.getMode?.() === 'draw_polygon';
  }

  private cancelPolygonDrawing(): boolean {
    if (!this.isDrawingPolygon()) return false;
    const draw = this.context.draw();
    draw.trash();
    if (this.isDrawingPolygon()) draw.changeMode('simple_select');
    return true;
  }

  private restore(state: MapHistoryState | null): void {
    const draw = this.context.draw();
    if (!state || !draw) return;
    this.restoring = true;
    try {
      this.context.beforeRestore();
      draw.changeMode('simple_select');
      draw.set(state.geojson);
      this.context.marker().setLngLat(state.center);
      this.context.recompute();
      this.context.unsavedChanged(this.history.dirty);
    } finally {
      this.restoring = false;
      this.recordedRevision = this.revision;
    }
  }
}
